import { useMutation } from "@tanstack/react-query";
import { EmailRequest, EmailResponse, EmailService } from "api/generated/cms";
import { t } from "i18next";
import { toast } from "react-toastify";
// Side-effect import: this is what points the generated client at the CMS base
// URL and attaches `x-fa-token` to every call, this POST included.
import "./cmsClient";

export interface IssuerEmailData {
  senderName: string;
  senderEmail: string;
  externalIds: string[];
  subject: string;
  text: string;
}

/**
 * Sends one message to one or more fund companies.
 *
 * Recipients are named by portal-side external id; the CMS resolves each id to
 * an address and fans the message out as a separate email per company, so
 * recipients never see each other. There is deliberately no attachment
 * support — `EmailRequest` has no field for it.
 *
 * Replaces the old SendGrid path (`api/email/usePostEmailIssuers`), which
 * posted to the portal's `send-emails` endpoint. The person-contact flow still
 * runs on that path; only issuers moved.
 */
const toRequest = (emailData: IssuerEmailData): EmailRequest => ({
  fromName: emailData.senderName,
  fromEmail: emailData.senderEmail,
  toExternalIds: emailData.externalIds,
  subject: emailData.subject,
  body: emailData.text,
});

export const usePostCmsEmailIssuers = () => {
  const { mutateAsync, isLoading, isError, data } = useMutation<
    EmailResponse,
    Error,
    IssuerEmailData
  >(
    (emailData) =>
      EmailService.postApiV1Email({ requestBody: toRequest(emailData) }),
    {
      onSuccess: () => {
        toast.success(t("contactModal.status.success"), {
          toastId: "contactModalSuccess",
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.error(t("contactModal.status.error"), {
          toastId: "contactModalError",
          autoClose: 5000,
        });
      },
    }
  );

  return {
    // `mutateAsync` rather than `mutate`: the modal must stay open (and keep
    // the user's typed message) when a send fails, which means the caller has
    // to be able to await the outcome.
    sendEmail: mutateAsync,
    isLoading,
    isError,
    data,
  };
};
